import React, { useMemo } from 'react';
import { Button, Drawer, useToasts } from 'freemium-ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  getDashboardAuth,
  useEditCurrentAccountUser,
} from 'routes/screens/dashboard/hooks';
import { useGetTenantInfo } from 'routes/screens/settings/hooks';
import { DrawerMenuContainer, DrawerMenuList } from 'shared/styles';
import { AccountUserType, DefaultDashboardTypes } from 'shared/types';
import Icon from '../Icon';

const ALL_DASHBOARDS: { id: DefaultDashboardTypes; label: string }[] = [
  {
    id: 'DEFAULT',
    label: 'dashboard.title',
  },
  {
    id: 'NPS',
    label: 'dashboard.all_dashboards.nps_dashboard',
  },
  {
    id: 'CES',
    label: 'dashboard.all_dashboards.ces_dashboard',
  },
  {
    id: 'CSAT',
    label: 'dashboard.all_dashboards.csat_dashboard',
  },
];

type DashboardBreadcrumpProps = {
  currentAccountUser: AccountUserType;
};

const DashboardBreadcrump = (props: DashboardBreadcrumpProps) => {
  const { t } = useTranslation();
  const { currentAccountUser } = props;
  const [, setToast] = useToasts();
  const [isDrawerOpen, toggleDrawer] = React.useState<boolean>(false);
  const [selectedDashboard, setSelectedDashboard] =
    React.useState<DefaultDashboardTypes>();

  const { data: tenantInfo } = useGetTenantInfo();

  const { dashboardList, isFreddyEnabled } = useMemo(() => {
    const isFreddyEnabled =
      tenantInfo?.account_meta?.enabled_features?.includes(
        'freddy_response_analysis',
      );
    const dashboards = [...ALL_DASHBOARDS];
    if (isFreddyEnabled) {
      dashboards.push({
        id: 'FREDDY_RESPONSE',
        label: 'dashboard.all_dashboards.freddy_dashboard',
      });
    }
    return { dashboardList: dashboards, isFreddyEnabled: isFreddyEnabled };
  }, []);

  const { refetch, isFetching } = getDashboardAuth({
    dashboard:
      selectedDashboard ||
      currentAccountUser.meta?.default_dashboard ||
      'DEFAULT',
    enabled: !!selectedDashboard,
  });

  const { mutate: editCurrentAccountUser, isLoading } =
    useEditCurrentAccountUser({
      userId: currentAccountUser?.user_id || '',
      callback: (isSuccess, response: AccountUserType) => {
        if (isSuccess && response.meta?.default_dashboard)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${t('forms.toast.success.default_dashboard', {
              theme:
                response.meta?.default_dashboard === 'FREDDY_RESPONSE'
                  ? t('dashboard.all_dashboards.freddy_dashboard')
                  : response.meta?.default_dashboard || 'DEFAULT',
            })}`,
          });
      },
    });

  const setAsDefaultDashboard = () => {
    editCurrentAccountUser({
      ...currentAccountUser,
      meta: {
        ...currentAccountUser?.meta,
        dashboard_dismissed: true,
        default_dashboard: selectedDashboard,
      },
    });
  };

  React.useEffect(() => {
    if (!selectedDashboard) {
      const defaultDashboard =
        currentAccountUser?.meta?.default_dashboard === 'FREDDY_RESPONSE'
          ? isFreddyEnabled
            ? 'FREDDY_RESPONSE'
            : 'DEFAULT'
          : currentAccountUser?.meta?.default_dashboard;
      setSelectedDashboard(defaultDashboard || 'DEFAULT');
    }
  }, [currentAccountUser, isFreddyEnabled]);

  React.useEffect(() => {
    refetch();
  }, [selectedDashboard]);

  const dashboard = dashboardList.find(
    (db) => db.id === selectedDashboard,
  )?.label;

  return (
    <StyledDashboardBreadcrump>
      <Button
        aria-label={`${t('sidebar.dashboard')}`}
        className="filter-button"
        inline
        type="secondary"
        onClick={() => toggleDrawer(true)}
      >
        <Icon width="13" height="13" glyph="filter" />
      </Button>
      <div className="dashboard-name">
        <span>{t(dashboard || '')}</span>
      </div>
      <div className="dash-tag">{t('dashboard.curated')}</div>
      {currentAccountUser?.meta?.default_dashboard !== selectedDashboard && (
        <Button
          inline
          type="secondary"
          disabled={isFetching || isLoading}
          size="mini"
          onClick={setAsDefaultDashboard}
        >
          {t('dashboard.default')}
        </Button>
      )}
      <Drawer
        isOpen={isDrawerOpen}
        onDismiss={() => toggleDrawer(false)}
        size="small"
        placement="left"
        hideDismissIcon
        customClass="in-app-drawer"
      >
        <DrawerMenuContainer>
          <DrawerMenuList>
            {dashboardList.map((db) => (
              <div
                key={db.id}
                className={`drawer-menu-item ${
                  selectedDashboard === db.id ? 'selected' : ''
                }`.trim()}
                onClick={() => {
                  toggleDrawer(false);
                  setSelectedDashboard(db.id);
                }}
              >
                <span>{t(db.label)}</span>
                {selectedDashboard === db.id && (
                  <Icon
                    fill="#000000"
                    glyph="black-tick"
                    height={13}
                    width={13}
                  />
                )}
              </div>
            ))}
          </DrawerMenuList>
        </DrawerMenuContainer>
      </Drawer>
    </StyledDashboardBreadcrump>
  );
};

const StyledDashboardBreadcrump = styled.div`
  display: flex;
  align-items: center;
  button.filter-button {
    padding: 4px;
    width: 32px;
  }
  button {
    margin: 0;
  }
  div.dashboard-name {
    font-weight: 600;
    font-size: 16px;
    line-height: 19px;
    color: #12344d;
  }
  div.dash-tag {
    padding: 2px 8px;
    background: #f5f7f9;
    border: 1px solid #bbdcfe;
    border-radius: 4px;
    font-weight: 600;
    font-size: 12px;
    line-height: 20px;
    color: #2c5cc5;
  }
  & > :not(:last-child) {
    margin-right: 16px;
  }
`;

export default DashboardBreadcrump;
